import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Divider, Stack, Typography,
} from '@mui/material';
import {
  OpenInFull as OpenIcon, Download as DownloadIcon, InsertDriveFileOutlined as EmptyIcon,
} from '@mui/icons-material';
import DocumentService, { DocumentType, TopOpsDocument } from '../../api/services/document.service';
import { DocumentPill, LockChip, ExpiryLine } from './vaultUI';
import { formatBytes, categoryLabel } from './vaultStatus';
import { downloadBlob } from '../../utils/download';

// The inspector: the selected document's card — thumbnail, the facts, and
// the two things you actually do with it.
//
// It used to fall back to a "Needs attention" worklist when nothing was
// selected, which put that list on screen three times at once on the Files
// home: the rail item carries the count as a badge, the compliance strip
// summarises it across the top of the page, and this was a third copy of
// the same rows. The rail is where you go to it and the strip is where you
// see how bad it is; the inspector's job is the file you clicked.

interface Props {
  document: TopOpsDocument | null;
}

const DocumentInspector: React.FC<Props> = ({ document }) => (
  <Box sx={{ p: document ? 2.5 : 0 }}>
    {document ? <SelectedDocument document={document} /> : <NothingSelected />}
  </Box>
);

const SelectedDocument: React.FC<{ document: TopOpsDocument }> = ({ document }) => {
  const navigate = useNavigate();
  const [type, setType] = useState<DocumentType | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setType(null);
    DocumentService.getDetail(document._id)
      .then((detail) => { if (active) setType(detail.type); })
      .catch(() => undefined);
    return () => { active = false; };
  }, [document._id]);

  // Only images get a thumbnail here. Rendering a PDF into a 300px panel
  // costs a full fetch to show something illegible; the full viewer is one
  // click away and does it properly.
  useEffect(() => {
    if (!document.file?.contentType?.startsWith('image/')) { setPreview(null); return undefined; }
    let url: string | null = null;
    let active = true;
    setFailed(false);
    DocumentService.getFileBlob(document._id)
      .then((blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        setPreview(url);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
      setPreview(null);
    };
  }, [document._id, document.file?.contentType]);

  const download = async () => {
    const blob = await DocumentService.getFileBlob(document._id);
    downloadBlob(blob, document.fileName);
    DocumentService.recordShared(document._id, 'download');
  };

  return (
    <Stack gap={2}>
      <Box
        sx={{
          height: 168, borderRadius: 3, bgcolor: 'grey.100',
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        }}
      >
        {preview ? (
          <Box component="img" src={preview} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : failed ? (
          <Typography variant="caption" color="text.disabled">Preview unavailable</Typography>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
            {document.file?.contentType?.includes('pdf') ? 'PDF' : 'File'}
          </Typography>
        )}
      </Box>

      <Box>
        <Typography variant="h6">{type?.label || categoryLabel(document.category)}</Typography>
        <Typography
          sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5, color: 'text.secondary', wordBreak: 'break-all' }}
        >
          {document.fileName}
        </Typography>
      </Box>

      <Stack direction="row" gap={1} flexWrap="wrap">
        <DocumentPill status={document.status} />
        {type?.sensitivity === 'restricted' && <LockChip />}
        {type?.sensitivity === 'confidential' && <LockChip label="Confidential" />}
      </Stack>

      <Stack direction="row" gap={1}>
        <Button fullWidth variant="contained" startIcon={<OpenIcon />} onClick={() => navigate(`/files/document/${document._id}`)}>
          Open
        </Button>
        <Button variant="outlined" onClick={download} aria-label="Download"><DownloadIcon fontSize="small" /></Button>
      </Stack>

      <Divider />

      <Stack gap={1.25}>
        <Field label="Filed against" value={document.ownerLabel || 'Company-wide'} />
        <Field label="Category" value={categoryLabel(document.category)} />
        <Box>
          <Typography variant="caption" color="text.disabled" display="block">Expires</Typography>
          {document.expiresAt
            ? <ExpiryLine expiresAt={document.expiresAt} />
            : <Typography variant="body2">Does not expire</Typography>}
        </Box>
        <Field label="Uploaded" value={document.uploadedAt ? new Date(document.uploadedAt).toLocaleDateString() : '—'} />
        <Field label="Size" value={formatBytes(document.file?.sizeBytes)} />
        {document.legalHold && <Alert severity="info" sx={{ mt: 1 }}>On legal hold</Alert>}
      </Stack>
    </Stack>
  );
};

const NothingSelected: React.FC = () => (
  <Stack alignItems="center" gap={1.25} sx={{ py: 7, px: 2.5, textAlign: 'center' }}>
    <EmptyIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
    <Typography variant="body2" color="text.secondary">
      Select a file to see its details.
    </Typography>
    <Typography variant="caption" color="text.disabled">
      Click a row once to inspect it, twice to open it.
    </Typography>
  </Stack>
);

const Field: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.disabled" display="block">{label}</Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

export default DocumentInspector;
